import React, { useRef, useState } from "react";
import { ScanLine, Camera } from "lucide-react";
import CameraScannerModal from "@/components/CameraScannerModal";

/**
 * BarcodeInput
 *
 * Two input modes:
 *  1. HID-keyboard hardware scanner — types into the input + presses Enter.
 *  2. Phone / tablet / webcam camera — click the camera button to open a
 *     fullscreen scanner that uses html5-qrcode to decode 1D + 2D barcodes.
 */
export default function BarcodeInput({ onScan, placeholder = "Scan or type barcode…", testid = "barcode-input", autoFocus = false, hint }) {
  const [value, setValue] = useState("");
  const [camOpen, setCamOpen] = useState(false);
  const ref = useRef(null);

  const submit = (override) => {
    const v = (override ?? value).trim();
    if (!v) return;
    onScan(v);
    setValue("");
    setTimeout(() => ref.current?.focus(), 0);
  };

  const handleCamera = (code) => {
    setCamOpen(false);
    submit(code);
  };

  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <div className="relative flex-1">
          <ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#002FA7]" />
          <input
            ref={ref}
            data-testid={testid}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); submit(); }
            }}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="w-full bg-white border-2 border-[#002FA7] text-sm pl-10 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
          />
        </div>
        <button
          type="button"
          data-testid={`${testid}-camera`}
          onClick={() => setCamOpen(true)}
          title="Use device camera to scan"
          className="border-2 border-[#002FA7] px-3 bg-white hover:bg-blue-50 transition-colors"
        >
          <Camera className="w-4 h-4 text-[#002FA7]" strokeWidth={2} />
        </button>
      </div>
      {hint && <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">{hint}</div>}

      {camOpen && (
        <CameraScannerModal
          onDetected={handleCamera}
          onClose={() => setCamOpen(false)}
        />
      )}
    </div>
  );
}
